import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _identity from "lodash/identity";
import _get from "lodash/get";
/**
 * @module
 *
 * @description Roundup selectors.
 */
import { TITLE_DATE_FORMATS } from '../constants';
import { getNwData, stripNwSuffix } from '../utils';
// eslint-disable-next-line import/no-cycle -- directive added automatically by Shepherd migration
import { getRoundupMonthAndYear, INVALID_DATE } from '../utils/date';

/**
 * Helper util to get Roundup date suffix.
 * @private - exported for testing.
 *
 * @param   {string} dateFormat   Date format from `TITLE_DATE_FORMATS`
 * @param   {Date} date           Custom date object
 *
 * @return  {string}              the date suffix string
 */
export var getRoundupDateSuffix = function getRoundupDateSuffix(dateFormat, date) {
  switch (dateFormat) {
    case TITLE_DATE_FORMATS.MONTH_AND_YEAR:
      {
        var monthAndYear = getRoundupMonthAndYear(date);
        return monthAndYear && monthAndYear !== INVALID_DATE ? " of ".concat(monthAndYear) : '';
      }
    default:
      return '';
  }
};

/**
 * Get custom content above sections.
 *
 * @param   {Object} roundup  WordPress Roundup.
 * @return  {string|Object}   HTML string or Structured Content Object for Roundup custom content
 *                              above sections.
 */
export var getCustomContentAboveSections = function getCustomContentAboveSections(roundup) {
  return _get(getNwData(roundup), 'custom_content.above_sections');
};

/**
 * Get custom content below sections.
 *
 * @param   {Object} roundup  WordPress Roundup.
 * @return  {string|Object}   HTML string or Structured Content Object for Roundup custom content
 *                              below sections.
 */
export var getCustomContentBelowSections = function getCustomContentBelowSections(roundup) {
  return _get(getNwData(roundup), 'custom_content.below_sections');
};

/**
 * Get whether or not featured product number should be included in the roundup title.
 *
 * @param   {Object} roundup  WordPress Roundup.
 * @return  {boolean}         Whether to include featured product number in the roundup title
 *
 */
export var getExcludeEntityCountFromTitle = function getExcludeEntityCountFromTitle(roundup) {
  return _get(getNwData(roundup), 'exclude_entity_count_from_nw_title');
};

/**
 * Get whether or not date should be inserted into roundup title.
 *
 * @param   {Object} roundup    WordPress Roundup.
 * @return  {boolean}           Whether to include the date in the roundup title
 */
export var getIncludeDateInTitle = function getIncludeDateInTitle(roundup) {
  return _get(getNwData(roundup), 'include_date_in_nw_title');
};

/**
 * Get whether or not date should be inserted into
 * SERP (Search Engine Results) title.
 *
 * @param   {Object} roundup    WordPress Roundup.
 * @return  {boolean}           Whether to include the date in the SERP title
 */
export var getIncludeDateInSeoTitle = function getIncludeDateInSeoTitle(roundup) {
  return _get(getNwData(roundup), 'include_date_in_nw_seo_title');
};

/**
 * Get the product type for the roundup.
 *
 * @param   {Object} roundup    WordPress Roundup.
 * @return  {string}            Roundup product type
 */
export var getMarketplaceProductType = function getMarketplaceProductType(roundup) {
  return _get(getNwData(roundup), 'marketplace_product_type');
};

/**
 * Get each of the roundup recommendation sections.
 *
 * @param   {Object} roundup    WordPress Roundup.
 * @return  {Array}             recommendation section list
 */
export var getRecommendationSections = function getRecommendationSections(roundup) {
  return _get(getNwData(roundup), 'roundup_sections');
};

/**
 * Get summary list quick facts.
 *
 * @param   {Object} entity     WordPress Roundup.
 *
 * @return  {Array}             quick fact name list
 */
export var getSummaryListQuickFacts = function getSummaryListQuickFacts(entity) {
  return _get(getNwData(entity), 'summary_list_quick_facts');
};

/**
 * Get Roundup summary list title.
 *
 * @param   {Object} roundup    WordPress Roundup.
 *
 * @return  {string}            Roundup summary list title.
 *
 */
export var getSummaryListTitle = function getSummaryListTitle(entity) {
  return _get(getNwData(entity), 'summary_list_title');
};

/**
 * Get Roundup synopsis.
 *
 * @param   {Object} roundup    WordPress Roundup.
 *
 * @return  {string|Object}     HTML string or Structured Content Object for Roundup synopsis.
 */
export var getSynopsis = function getSynopsis(roundup) {
  return _get(getNwData(roundup), 'synopsis');
};

/**
 * Get entity title.
 *
 * @param   {Object} entity     WordPress Roundup.
 * @param   {Object} opts       optionals
 *            {string} dateFormat
 *            {func} preProcessFunc
 *            {Date} customDate (should only be used for testing)
 * @return  {string|undefined}            Title.
 */
export var getTitle = function getTitle(entity) {
  var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var dateFormat = opts.dateFormat || getIncludeDateInTitle(entity);
  var preProcessFunc = opts.preProcessFunc || _identity;
  var title = preProcessFunc(_get(entity, 'title.rendered_text'));
  if (title) {
    var dateSuffix = getRoundupDateSuffix(dateFormat, opts.customDate);
    return "".concat(title).concat(dateSuffix);
  }
  return undefined;
};

/**
 * Get entity seo title.
 * @note appends `- NerdWallet` automatically
 *
 * @param   {Object} entity     WordPress Roundup.
 * @param   {Object} opts       optionals
 *            {Date} customDate (should only be used for testing)
 * @return  {string|undefined}            SEO title or undefined if fallback fails.
 */
export var getTitleSeo = function getTitleSeo(entity) {
  var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var dateFormat = getIncludeDateInSeoTitle(entity);
  var seoTitle = _get(entity, 'nw_seo.title');
  var fallbackTitle = getTitle(entity, _objectSpread({
    dateFormat: dateFormat,
    preProcessFunc: stripNwSuffix
  }, opts));
  if (seoTitle) {
    var dateSuffix = getRoundupDateSuffix(dateFormat, opts.customDate);
    return "".concat(stripNwSuffix(seoTitle)).concat(dateSuffix, " - NerdWallet");
  }
  if (fallbackTitle) {
    return "".concat(fallbackTitle, " - NerdWallet");
  }
  return undefined;
};

/**
 * Get whether or not to use alp information for ordering.
 *
 * @param   {Object} roundup  WordPress Roundup.
 * @return  {boolean}         Whether to use alp ordering
 *
 */
export var getUseAlpOrdering = function getUseAlpOrdering(roundup) {
  return _get(getNwData(roundup), 'use_alp_ordering_for_sections');
};